import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useState } from 'react';
import DateRange from '../../Common/DateRange';
import KaAlert from '../../Common/KaAlert';
import KaModal from '../../Common/KaModal';
import { isMultiValue, KaSelect, KaSelectOption } from '../../Common/KaSelect';
import { useDisplayMessage } from '../../Common/useDisplayMessage';
import { FilterSettings } from './OrderTransactionsListPage';

export interface OrderTransactionSearchModalProps {
  show: boolean;
  itemOptions: KaSelectOption[];
  siteOptions: KaSelectOption[];
  partnerOptions: KaSelectOption[];
  filters: FilterSettings;
  updateFilters: (settings: Partial<FilterSettings>) => void;
  onClose: () => void;
}

const OrderTransactionSearchModal = ({
  show,
  itemOptions,
  siteOptions,
  partnerOptions,
  filters,
  updateFilters,
  onClose,
}: OrderTransactionSearchModalProps) => {
  const displayMessage = useDisplayMessage();
  const [selectedItem, setSelectedItem] = useState<string>(filters.item);
  const [selectedSite, setSelectedSite] = useState<string>(filters.site);
  const [selectedPartner, setSelectedPartner] = useState<string>(filters.partner);

  const dateFormat = 'MM/DD/YYYY';
  const toMydString = (date: Dayjs): string => date.format(dateFormat);
  const [dateFilterChecked, setDateFilterChecked] = useState(!!filters.startDate);
  const [selectedDates, setSelectedDates] = useState({
    startDate: dateFilterChecked ? filters.startDate : toMydString(dayjs().subtract(1, 'week')),
    endDate: dateFilterChecked ? filters.endDate : toMydString(dayjs())
  });

  useEffect(() => {
    setSelectedItem(filters.item);
    setSelectedSite(filters.site);
    setSelectedPartner(filters.partner);
    setDateFilterChecked(!!filters.startDate);
    setSelectedDates({
      startDate: filters.startDate || toMydString(dayjs().subtract(1, 'week')),
      endDate: filters.endDate || toMydString(dayjs())
    });
  }, [filters]);

  const handleSearch = () => {
    updateFilters({
      item: selectedItem,
      site: selectedSite,
      partner: selectedPartner,
      startDate: dateFilterChecked ? selectedDates.startDate : '',
      endDate: dateFilterChecked ? selectedDates.endDate : ''
    });
    onClose();
  };

  return (
    <KaModal
      onHide={onClose}
      show={show}
      title={"Search Criteria for Transactions"}
      body={
        <div>
          <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />

          <div className="row">
            <h5>Product/Recipe:</h5>
            <KaSelect
              onChange={(valueObject) => {
                if (!isMultiValue(valueObject)) {
                  setSelectedItem(valueObject?.value!);
                }
              }}
              placeholder="Select product or recipe"
              isClearable={true}
              options={itemOptions}
              value={itemOptions.find((option) => option.value === selectedItem)} />
          </div>

          <div className="row mt-3">
            <h5>Site:</h5>
            <KaSelect
              onChange={(valueObject) => {
                if (!isMultiValue(valueObject)) {
                  setSelectedSite(valueObject?.value!);
                }
              }}
              placeholder="Select site"
              isClearable={true}
              options={siteOptions}
              value={siteOptions.find((option) => option.value === selectedSite)} />
          </div>

          {partnerOptions.length > 0 && (
            <div className="row mt-3">
              <h5>Partner Site:</h5>
              <KaSelect
                onChange={(valueObject) => {
                  if (!isMultiValue(valueObject)) {
                    setSelectedPartner(valueObject?.value!);
                  }
                }}
                placeholder="Select partner site"
                isClearable={true}
                options={partnerOptions}
                value={partnerOptions.find((option) => option.value === selectedPartner)} />
            </div>
          )}

          <div className="row mt-3">
            <div className="form-check ms-3">
              <input
                type="checkbox"
                id="toggleDateFilter"
                className="form-check-input"
                checked={dateFilterChecked}
                onChange={(e) => setDateFilterChecked(e.target.checked)} />
              <label htmlFor="toggleDateFilter" className="form-check-label">
                Filter by date range
              </label>
            </div>
            {dateFilterChecked && (
              <DateRange
                className="row mt-3"
                startDate={selectedDates.startDate}
                endDate={selectedDates.endDate}
                rangeUpdated={(startDate, endDate) => setSelectedDates({ startDate, endDate })}
                dateFormat={dateFormat} />
            )}
          </div>
        </div>
      }
      footer={
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSearch}>
          Search
        </button>
      }
    />
  );
};

export default OrderTransactionSearchModal;